import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, defer, map, of, shareReplay } from 'rxjs';

/**
 * The Sparte's insurers, fetched once and replayed for the app's lifetime. The app subclass supplies
 * the generated transport, so this lib names no generated type and the app's own `Versicherer` flows
 * through `T` — callers keep every field, `logo` included.
 */
@Injectable()
export abstract class VersichererCacheBaseService<T extends { bafinRegNr: string }> {
  /** The app's generated call, e.g. `() => versichererService.getVersicherer({ sparte })`. */
  protected abstract readonly fetch$: () => Observable<T[]>;

  /**
   * `defer` is load-bearing: this initializer runs before the subclass has assigned `fetch$`, so the
   * call must not be made until something subscribes.
   */
  readonly versicherers$: Observable<T[]> = defer(() => this.fetch$()).pipe(
    // A dead insurer list must not blank out every card's logo.
    catchError(() => of<T[]>([])),
    shareReplay(1)
  );

  readonly findVersicherer$ = (bafinRegNr: string): Observable<T | undefined> =>
    this.versicherers$.pipe(map(list => list.find(v => v.bafinRegNr === bafinRegNr)));
}
